import { useMutation } from '@tanstack/react-query';

import type { SystemUserQuerySchema } from '@/_pages/system-users/shared';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type { CustomSettingsQuerySchema } from './schemas/custom-settings-query-schema';
import type { CreateCustomSettingPayload, UpdateCustomSettingPayload } from './types';

const API_ENDPOINT = '/api/v1/custom-settings';

// Helper function to safely parse JSON value
export function parseCustomSettingValue<T>(value: string, defaultValue?: T): T | null {
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    console.error(`Failed to parse custom setting value: ${value}`, error);
    return defaultValue ?? null;
  }
}

// Helper function to stringify value for API
export function stringifyCustomSettingValue<T>(value: T): string {
  try {
    if (typeof value === 'string') return value;
    return JSON.stringify(value);
  } catch (error) {
    console.error('Failed to stringify custom setting value:', value, error);
    throw new Error('Invalid custom setting value');
  }
}

// Create custom setting mutation
export function useCreateCustomSetting() {
  const user = queryClient.getQueryData<SystemUserQuerySchema>(['CURRENT_USER']);

  return useMutation<{ data: CustomSettingsQuerySchema }, ApiError, { name: string; value: unknown }>({
    mutationFn: async ({ name, value }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const payload: CreateCustomSettingPayload = {
        name,
        value: stringifyCustomSettingValue(value),
        user_id: user.id,
      };

      return apiClient.request<{ data: CustomSettingsQuerySchema }, CreateCustomSettingPayload>({
        endpoint: API_ENDPOINT,
        method: 'POST',
        payload,
      });
    },
  });
}

// Update custom setting mutation
export function useUpdateCustomSetting() {
  return useMutation<{ data: CustomSettingsQuerySchema }, ApiError, { id: number; value: unknown }>({
    mutationFn: async ({ id, value }) => {
      const payload: UpdateCustomSettingPayload = {
        value: stringifyCustomSettingValue(value),
      };

      return apiClient.request<{ data: CustomSettingsQuerySchema }, UpdateCustomSettingPayload>({
        endpoint: `${API_ENDPOINT}/${id}`,
        method: 'PUT',
        payload,
      });
    },
  });
}

// Delete custom setting mutation
export function useDeleteCustomSetting() {
  return useMutation<unknown, ApiError, { id: number }>({
    mutationFn: async ({ id }) => {
      return apiClient.request({
        endpoint: `${API_ENDPOINT}/${id}`,
        method: 'DELETE',
      });
    },
  });
}

// Helper function to update cached setting optimistically
export function updateCachedCustomSetting(
  name: string,
  value: string | null | undefined,
  newValue?: CustomSettingsQuerySchema
) {
  const user = queryClient.getQueryData<SystemUserQuerySchema>(['CURRENT_USER']);
  if (!user?.id) return;

  queryClient.setQueryData<SystemUserQuerySchema>(['CURRENT_USER'], (oldData) => {
    if (!oldData || !oldData.custom_settings) return oldData;

    // Check if the setting exists
    const existingIndex = oldData.custom_settings.findIndex((setting) => setting.name === name);

    let newCustomSettings;
    if (value === null) {
      // Remove the setting if value is null/undefined
      newCustomSettings = oldData.custom_settings.filter((setting) => setting.name !== name);
    } else if (existingIndex !== -1 && value) {
      // Update the setting if it exists
      newCustomSettings = oldData.custom_settings.map((setting) =>
        setting.name === name ? { ...setting, value: typeof value === 'string' ? JSON.parse(value) : value } : setting
      );
    } else if (newValue) {
      // Add the new setting if it doesn't exist
      newCustomSettings = [
        ...oldData.custom_settings,
        { ...newValue, value: typeof newValue.value === 'string' ? JSON.parse(newValue.value) : newValue.value },
      ];
    }

    return {
      ...oldData,
      custom_settings: newCustomSettings,
    };
  });
}
